import {Injectable} from '@angular/core';
import {AbstractControl, FormArray, FormControl, FormGroup} from "@angular/forms";
import {ENoticeTypeXsd} from "../../../../models/enums/enotice-type-xsd.enum";
import {DuplicableElementPartialPathsEnum} from "../enum/duplicable-element-partial-paths.enum";
import cloneDeep from 'lodash.clonedeep';
import { DuplicableObjectArrayEnum } from '../enum/duplicable-object-array.enum';


@Injectable({
  providedIn: 'root'
})
export class EformDataService {

  constructor() {
  }

  /**
   * Récupére un objet dans noticeValue à partir d'un path
   * @param jsonPath
   * @param noticeValue
   */
  getFormArrayFromNoticeValue(jsonPath, noticeValue, haveToPop = false) {
    let splittedJsonPath = jsonPath.toLowerCase().split('.');
    if (haveToPop) {
      splittedJsonPath.pop();
    }
    const itemForm = splittedJsonPath.reduce((p, q) => {
      if (q.includes('[]')) {
        q = q.replace('[]', '');
      }
      if ((p instanceof Array && p.length > 0) || (p && Object.keys(p).length > 0 && Object.keys(p).every(key => Number.isInteger(Number(key))))) {
        return p[0][q];
      }
      return p ? p[q] : null;
    }, noticeValue);
    return itemForm;
  }

  /**
   * Récupère un objet dans noticeValue à partir d'un path en tenant compte du contexte
   * du repeatablePathWithIndex pour naviguer vers le bon élément duplicable parent
   * @param jsonPath Le chemin JSON vers l'élément
   * @param noticeValue La valeur de la notice
   * @param repeatablePathWithIndex Le chemin complet avec les index
   * @param haveToPop Indique s'il faut retirer le dernier élément du chemin
   */
  getFormArrayFromNoticeValueWithContext(jsonPath, noticeValue, repeatablePathWithIndex, haveToPop = false) {
    let splittedJsonPath = jsonPath.toLowerCase().split('.');
    if (haveToPop) {
      splittedJsonPath.pop();
    }

    const itemForm = splittedJsonPath.reduce((p, q) => {
      if (q.includes('[]')) {
        q = q.replace('[]', '');
      }
      if ((p instanceof Array && p.length > 0) || (p && Object.keys(p).length > 0 && Object.keys(p).every(key => Number.isInteger(Number(key))))) {
        // ✅ FIX 2026-01-08: Utiliser getArrayIndex pour récupérer le bon index du parent
        // au lieu de toujours utiliser p[0]
        const index = this.getArrayIndex(q + '[]', repeatablePathWithIndex);
        const actualIndex = (index !== -1 && index !== undefined) ? index : 0;

        return p[actualIndex]?.[q] ?? p[0]?.[q];
      }
      return p ? p[q] : null;
    }, noticeValue);
    return itemForm;
  }

  /**
   * Ajoutes des objets en debut de tableau dans noticeValue à partir d'un path
   * @param jsonPath
   * @param noticeValue
   */
  addObjectsAtPath<T>(obj: T, path: string, newItems: any[]): T {
    const keys = path.toLowerCase().split('.'); // Divise le chemin en clés
    const lastKey = keys.pop(); // Dernière clé pour accéder au tableau
    let current = obj as any; // Commence à la racine de l'objet

    // Naviguer jusqu'à l'avant-dernier niveau
    for (const key of keys) {
      if (current instanceof Array) {
        current = current[0]; //On va toujours dans le premier dans ce cas
      }
      if (!current || !(key in current)) {
        throw new Error(`Invalid path: "${path}"`);
      }
      current = current[key];
    }

    // Vérifie que la clé finale est bien un tableau
    if (lastKey && current && Array.isArray(current[lastKey])) {
      if (DuplicableElementPartialPathsEnum["GR-Organisations"] === path && current[lastKey].length >= 1) {
        //Cas spécial pour les organisations, il y en a parfois une ou deux de générées initialement
        //Le systéme back est conçu pour récupérer la premiére en tant qu'init
        //Dans ce cas, on ajoute notre organisation préremplie en 2eme position (index 1)
        if (current[lastKey].length >= 1) {
          current[lastKey].splice(1, 0, ...newItems);
        } else {
          // Si aucune organisation n'existe, on les ajoute simplement
          current[lastKey].push(...newItems);
        }

        //Enfin on remet un peut d'ordre dans les ids
        const initializedPartyIdentification = current[lastKey][0].company.partyidentification;

        current[lastKey].forEach((orga, index: number) => {
          //L'organisation préremplie ne contient pas la partie identification, on l'ajoute pour que la sauvegarde fonctionne
          orga.company.partyidentification = cloneDeep(initializedPartyIdentification);

          // Attribution des IDs selon l'index :
          // Index 0 → ORG-0001 (init)
          // Index 1 → ORG-0001 (première org courante, même ID que l'init)
          // Index 2 → ORG-0002 (deuxième org courante)
          // Index 3 → ORG-0003 (troisième org courante)
          if (index === 0 || index === 1) {
            // Les deux premières organisations gardent ORG-0001
            orga.company.partyidentification.id.val = "ORG-0001";
          } else {
            // À partir de l'index 2, on attribue ORG-0002, ORG-0003, etc.
            const newId = "ORG-" + index.toString().padStart(4, '0');
            orga.company.partyidentification.id.val = newId;
          }
        });
      } else {
        current[lastKey].push(...newItems); // Ajoute les nouveaux objets au tableau
      }
    } else if (lastKey && current && (current[lastKey] === undefined || current[lastKey] === null)) {
      // ✅ FIX 2026-03-25: Lors de la création (ex: F29 via API de redirection), le tableau
      // peut ne pas encore être initialisé (ex: procurementProjectLot est undefined dans new ContractAwardNotice()).
      // Dans ce cas, on initialise le tableau avec les nouveaux éléments au lieu de lancer une erreur.
      current[lastKey] = [...newItems];
    } else {
      throw new Error(`Path does not lead to a valid array: "${path}"`);
    }

    return obj; // Retourne l'objet modifié
  }

  /**
   * Supprime des objets d'un tableau dans noticeValue à partir d'un path
   * @param path - Le chemin vers le tableau à vider
   * @param obj - L'objet JSON à modifier
   * @returns L'objet original, avec le tableau vidé si le path est valide
   */
  clearArrayAtPath<T>(obj: T, path: string, onlyFirstRowToDelete: boolean): T {
    const keys = path.toLowerCase().split('.'); // Divise le chemin en clés
    const lastKey = keys.pop(); // Dernière clé pour accéder au tableau
    let current = obj as any; // Commence à la racine de l'objet

    // Naviguer jusqu'à l'avant-dernier niveau
    for (const key of keys) {
      if (current instanceof Array) {
        current = current[0]; // On va toujours dans le premier dans ce cas
      }

      if (!current || !(key in current)) {
        return obj; // Retourne l'objet original si une clé est introuvable
      }

      current = current[key];
    }

    // Vérifie que la clé finale est bien un tableau
    if (lastKey && Array.isArray(current[lastKey])) {
      if(!onlyFirstRowToDelete){
        current[lastKey].length = 0; // Vide le tableau en gardant sa référence
      }else{
        current[lastKey].shift() // Vide le premier element du tableau
      }
    }

    return obj; // Retourne l'objet original, modifié uniquement si le tableau a été vidé
  }

  /**
   * Récupérer l'indexe à partir du nom de l'élément concerné par la répétition et du repeatablePathWIthIndex
   * @param element
   * @param repeatablePathWithIndex
   * @param existIndex
   */
  getArrayIndex(element, repeatablePathWithIndex, existIndex?) {
    repeatablePathWithIndex = repeatablePathWithIndex?.replaceAll(".val_", "_");

    // ✅ FIX 2026-01-09: Pour les éléments duplicables majeurs, on utilise TOUJOURS l'index 0
    // car après loadResult/loadOrganisation/etc., le noticeValue ne contient qu'UN SEUL élément.
    // Le repeatablePathWithIndex peut contenir des index d'éléments enfants (ex: strategicprocurement[]_INDEX_4)
    // qui ne sont PAS l'index du parent duplicable majeur.
    const majorDuplicableElements = ['lotresult', 'organization', 'lottender', 'tenderingparty', 'settledcontract'];
    const baseElement = element.replace('[]', '').toLowerCase();

    // ✅ FIX 2026-03-24: Quand lottender ou settledcontract sont imbriqués dans lotresult
    // (GR-LotResult-Tender-Id-Ref / GR-LotResult-Contract-Id-Ref), ce sont des sous-éléments
    // répétables, pas des éléments duplicables principaux. Ils doivent utiliser leur index réel
    // depuis le repeatablePathWithIndex au lieu d'être forcés à 0.
    const isNestedInLotResult = (baseElement === 'lottender' || baseElement === 'settledcontract')
      && repeatablePathWithIndex?.toLowerCase().includes('lotresult[]');

    if (majorDuplicableElements.includes(baseElement) && !isNestedInLotResult) {
      // Chercher spécifiquement l'index de CET élément, pas d'un enfant
      // Le pattern correct est: element[]_INDEX_X ou element[].val_INDEX_X (déjà remplacé par element[]_INDEX_X)
      const specificPattern = new RegExp(`${baseElement}\\[\\]_INDEX_(\\d+)`, 'i');
      const specificMatch = repeatablePathWithIndex?.match(specificPattern);
      if (specificMatch) {
        return specificMatch[1];
      }
      // Si pas de pattern spécifique trouvé, retourner 0 (l'élément unique après load)
      return 0;
    }

    if (element.includes("ublextension")) {
      return 0;
    }
    if (repeatablePathWithIndex?.match(/_INDEX_/g)?.length === 1) {
      return repeatablePathWithIndex.split('_INDEX_')[1];
    } else if (repeatablePathWithIndex?.includes(element + '_INDEX_')) {
      return repeatablePathWithIndex.split(element + '_INDEX_')[1].split('_')[0];
    } else if (repeatablePathWithIndex?.includes(element + '.id_INDEX_')) {
      return repeatablePathWithIndex.split(element + '.id_INDEX_')[1].split('_')[0];
    } else if (!repeatablePathWithIndex?.split(element + '.')[1]?.includes('.')
      && !repeatablePathWithIndex?.split(element + '.')[1]?.includes('[]')
      && repeatablePathWithIndex?.split(element + '.')[1]?.includes('_INDEX_')) {
      return repeatablePathWithIndex?.split(element + '.')[1]?.split('_INDEX_')[1]?.split('_')[0];
    }

    // Mise en place de ce cas lorsqu'on a un élément avec des crochets, on récupère l'index du première index qui suit.
    // On n'applique pas cette logique quand on a un element appartenant à DuplicableObjectArrayEnum.
    const regex = new RegExp(`${baseElement}\\[\\](?:\\.[^.\\[]+)*?_INDEX_(\\d+)`);
    const match = repeatablePathWithIndex?.match(regex);

    const enumKeys = Object.values(DuplicableObjectArrayEnum)
          .filter((v): v is string => typeof v === "string")
          .map(v => v.toLowerCase());

    if (match && !enumKeys.includes(baseElement)) {
      return match[1];
    }

    if (existIndex) {
      return -1;
    }
    return 0;
  }

  /**
   * Recupére l'indexe du prochain element à créer
   * @param actual
   * @param formGroup
   * @param repeatablePathWithIndex
   * @param tempJsonPath
   * @param objectToVerify
   * @param noticeValue
   * @param temporaireWithoutId
   * @param eformConditionService
   */
  getArray(actual, formGroup, repeatablePathWithIndex, tempJsonPath?, objectToVerify?, noticeValue?, temporaireWithoutId?, eformConditionService?, isForDelete?) {
    let tempControl;
    let i = 0;
    let pathElements = tempJsonPath?.split('.') ?? actual.jsonPath.split('.');
    if (!tempJsonPath) {
      pathElements.pop();
    }
    pathElements.forEach(elements => {
      if (i == 0) {
        tempControl = elements.includes('[]') ? (<any>formGroup).controls[elements.replace('[]', '')].controls[0]
          : formGroup.controls[elements];
      } else {
        if (elements.includes('[]')) {
          if (i == pathElements.length - 1) {
            tempControl = tempControl?.controls[elements.replace('[]', '')]
          } else {
            tempControl = tempControl?.controls[elements.replace('[]', '')]?.controls[this.getArrayIndex(elements, repeatablePathWithIndex)];
          }
        } else {
          if (Array.isArray(tempControl?.controls)) {
            tempControl = tempControl?.controls[this.getArrayIndex(elements, repeatablePathWithIndex)]?.controls[elements.replace('[]', '')];
          } else {
            tempControl = tempControl?.controls[elements];
          }
        }
      }
      i++;
    });

    if (temporaireWithoutId && tempControl) {
      let tempControls = tempControl;
      if (objectToVerify.conditionsCreationMap) {
        tempControls = eformConditionService.filterFormArrayWithCondition(tempControls, objectToVerify.conditionsCreationMap);
      }
      if (tempControls?.controls?.length == 1) {
        return tempControls?.controls.filter(control => control.value.id?.val != '').length
      }
    }
    let index;
    // Le but ici est de, quand on est dans un cas de suppression, trouver l'index
    // qui correspondant à l'élement qu'on est entrain de supprimer. Cela permet de
    // gérer les cas ou on a des éléments répétable qui ont les mêmes paths que
    // d'autre élements.
    if(isForDelete) {
      const actualId = actual.id;
      index = tempControl?.controls?.findIndex(ctrl => {
        const idInput = ctrl?.idInput;
        if (!idInput) return false;
        const lastPart = idInput.includes('|') ? idInput.split('|').pop() : idInput;
        return lastPart === actualId;
      });

    } else {
      // ✅ Distinction entre éléments duplicables principaux et imbriqués
      const isDuplicablePrincipal = this.isDuplicableElementPrincipal(tempJsonPath ?? actual.jsonPath);

      // ✅ FIX: Les tableaux imbriqués dans organization (partylegalentity, ultimatebeneficialowner, partyname)
      // doivent retourner 0 car ils sont propres à chaque organisation.
      // Sinon, controls.length retourne le nombre total de contrôles créés (ex: 1 après ORG-0001),
      // ce qui cause la création d'un index 1 au lieu de 0 pour ORG-0002.
      const isNestedInOrg = this.isNestedArrayInOrganization(tempJsonPath ?? actual.jsonPath);

      if (isDuplicablePrincipal || isNestedInOrg) {
        index = 0;
      } else {
        index = tempControl?.controls?.length ?? 0;
      }
    }

    return index;
  }

  /**
   * Vérifie si le jsonPath correspond à un tableau imbriqué dans une organisation.
   * Ces tableaux doivent toujours utiliser l'index 0 car ils sont propres à chaque organisation.
   */
  private isNestedArrayInOrganization(jsonPath: string): boolean {
    if (!jsonPath) {
      return false;
    }

    const lowerPath = jsonPath.toLowerCase();

    // Vérifier si le chemin contient organization et un tableau imbriqué
    if (!lowerPath.includes('organization[]') && !lowerPath.includes('organization[].')) {
      return false;
    }

    // Liste des tableaux imbriqués dans organization
    const nestedArrays = ['partylegalentity[]', 'ultimatebeneficialowner[]', 'partyname[]'];

    // Vérifier si le dernier tableau du chemin est un de ces tableaux imbriqués
    const pathParts = lowerPath.split('.');
    pathParts.reverse();
    const lastArrayPart = pathParts.find(part => part.includes('[]'));

    return lastArrayPart ? nestedArrays.includes(lastArrayPart) : false;
  }


  /**
   * Vérifie si le jsonPath correspond à un élément duplicable principal
   * (LOT, ORG, CON, TEN, TPA, RES) qui est chargé un par un depuis le backend.
   * @param jsonPath Le chemin JSON à vérifier
   * @returns true si c'est un élément duplicable principal, false sinon
   */
  private isDuplicableElementPrincipal(jsonPath: string): boolean {
    if (!jsonPath) {
      return false;
    }

    // Extraire le dernier élément de tableau du path
    // Ex: "procurementprojectlot[].tenderingterms.awardingterms.awardingcriterion[].subordinateawardingcriterion[]"
    // → dernier tableau = "subordinateawardingcriterion[]"
    const pathParts = jsonPath.split('.');
    pathParts.reverse();
    const lastArrayPart = pathParts.find(part => part.includes('[]'));

    if (!lastArrayPart) {
      return false;
    }

    // Retirer les [] pour obtenir le nom
    const lastArrayName = lastArrayPart.replace('[]', '').toLowerCase();

    // ✅ FIX 2026-03-24: Quand lottender[] ou settledcontract[] sont imbriqués dans lotresult[],
    // ce sont des sous-éléments répétables (GR-LotResult-Tender-Id-Ref / GR-LotResult-Contract-Id-Ref),
    // pas des éléments duplicables principaux. Ils doivent utiliser l'indexation incrémentale
    // pour que chaque "Ajouter nouveau" crée un nouvel entry dans le FormArray au lieu d'écraser l'index 0.
    if ((lastArrayName === 'lottender' || lastArrayName === 'settledcontract')
        && jsonPath.toLowerCase().includes('lotresult[]')) {
      return false;
    }

    // Vérifier si ce nom correspond à un élément duplicable principal
    const duplicablePaths = Object.values(DuplicableElementPartialPathsEnum);
    return duplicablePaths.some(partialPath => {
      const pathName = partialPath.split('.').pop()?.replace('[]', '').toLowerCase();
      return pathName === lastArrayName;
    });
  }

  /**
   * Retourne les paths pour lesquels la valeur à la fin du chemin est vide (null, undefined, ou chaîne vide)
   * @param control
   * @param path
   * @param emptyPaths
   */
  private getEmptyValuePathsFromForm(control: AbstractControl, path: string, emptyPaths: string[]): void {
    const parts = path.split('.');

    // Fonction récursive pour parcourir la structure du formulaire
    function recursiveSearch(
      currentControl: AbstractControl | undefined,
      pathParts: string[],
      currentPath: string
    ): void {
      // Vérification : Si le contrôle est undefined ou null, on arrête la recherche pour ce chemin
      if (!currentControl) {
        return;
      }

      // Si on est à la fin du chemin
      if (pathParts.length === 0) {
        // Vérifier si la valeur actuelle est vide (null, undefined, ou chaîne vide)
        if (currentControl instanceof FormControl &&
          (currentControl.value === null || currentControl.value === undefined || currentControl.value === '')) {
          emptyPaths.push(currentPath.slice(1)); // Ajouter le chemin si la valeur est vide
        }
        return;
      }

      // Prendre la première partie du chemin
      let currentKey = pathParts[0];
      const isArray = currentKey.endsWith('[]');

      if (isArray) {
        // Gérer les FormArray : retirer '[]' pour avoir la clé réelle
        currentKey = currentKey.slice(0, -2);

        const nextControl = (currentControl as FormGroup).controls[currentKey];
        if (nextControl instanceof FormArray) {
          // Parcourir chaque élément du FormArray
          nextControl.controls.forEach((control, index) => {
            recursiveSearch(control, pathParts.slice(1), `${currentPath}.${currentKey}[${index}]`);
          });
        }
      } else {
        // Sinon, gérer les FormGroup et FormControl
        const nextControl = (currentControl as FormGroup).controls[currentKey];
        // Vérification supplémentaire si le contrôle existe avant de continuer
        if (nextControl) {
          recursiveSearch(nextControl, pathParts.slice(1), `${currentPath}.${currentKey}`);
        }
      }
    }

    // Démarrer la recherche avec le chemin complet
    recursiveSearch(control, parts, '');
  }

  /**
   * Supprime un contrôle ou un tableau entier à partir d'un chemin donné dans le formulaire.
   *
   * @param {AbstractControl} control - Le contrôle de formulaire à examiner.
   * @param {string} path - Le chemin du contrôle à supprimer.
   * @returns {boolean} - Indique si un contrôle a été supprimé.
   */
  public removeLastControl(control: AbstractControl, path: string): AbstractControl {
    const parts = path.split('.');
    let completeControl: AbstractControl = control;
    // Appel de la fonction de recherche récursive
    if (!this.recursiveSearchAndDeleteLastControl(completeControl, parts)) {
      console.warn("Can't find data to delete: " + parts[parts.length - 1]);
    }
    return completeControl;
  }

  /**
   * Fonction récursive pour trouver et supprimer le dernier contrôle ou un tableau entier.
   *
   * @param {AbstractControl | undefined} currentControl - Le contrôle actuel à examiner.
   * @param {string[]} pathParts - Les parties du chemin à suivre.
   * @returns {boolean} - Indique si un contrôle ou un tableau a été supprimé.
   */
  private recursiveSearchAndDeleteLastControl(currentControl: AbstractControl | undefined, pathParts: string[]): boolean {
    if (!currentControl) {
      return false;
    }

    if (pathParts.length === 0) {
      return false; // On ne fait rien si on est déjà à la fin du chemin
    }

    const currentKey = pathParts[0];
    const isArray = currentKey.endsWith(']');

    if (isArray) {
      const keyWithoutArray = currentKey.split('[')[0]; // Retirer '[x]' pour avoir la clé réelle
      let index = +currentKey.split('[')[1].replace(']', '');// Récupèration de l'index du tableau

      if (currentControl instanceof FormGroup) {
        let nextControl = currentControl.controls[keyWithoutArray];

        if (nextControl instanceof FormArray && pathParts.length === 1) {
          // Supprimer complètement le tableau si c'est la fin du chemin
          currentControl.removeControl(keyWithoutArray);
          return true; // Tableau supprimé
        } else if (nextControl instanceof FormArray) {
          nextControl = nextControl.controls[index];
          return this.recursiveSearchAndDeleteLastControl(nextControl, pathParts.slice(1)); // Continuer la recherche
        }
      }
    } else {
      const nextControl = (currentControl instanceof FormGroup) ? currentControl.controls[currentKey] : undefined;

      if (nextControl) {
        if (pathParts.length === 1) {
          // Utilisation de removeControl à la place de delete
          if (currentControl instanceof FormGroup) {
            currentControl.removeControl(currentKey); // Supprimer le contrôle
            return true; // Contrôle supprimé
          }
        } else {
          return this.recursiveSearchAndDeleteLastControl(nextControl, pathParts.slice(1)); // Continuer la recherche
        }
      }
    }
    return false; // Aucun contrôle trouvé à ce chemin
  }

  //parcours un path et si un élement n'existe pas, le crée à vide
  ensurePropertyPath(obj: any, path: string[]): any {
    return path.reduce((acc, key) => {
      if (!acc[key]) {
        acc[key] = isNaN(Number(key)) ? {} : []; // Crée un tableau si la clé est un indice numérique
      }
      return acc[key];
    }, obj);
  }

  /**
   * ✅ FIX 2026-01-09: Supprime les contrôles d'un élément duplicable majeur (RES, ORG, etc.) dans le FormGroup.
   *
   * Quand on charge un nouvel élément duplicable, il faut SUPPRIMER les anciens contrôles
   * pour éviter l'accumulation de doublons (comme receivedSubmissionsStatistics x3).
   *
   * Le formulaire sera automatiquement recréé avec les bonnes données lors du rendu.
   *
   * @param formGroup Le FormGroup principal (eforms)
   * @param type Le type d'élément duplicable ('RES', 'ORG', 'TEN', 'CON', 'TPA')
   */
  clearDuplicableElementControls(formGroup: FormGroup, type: string, noticeTypeName?: string): void {
    if (!formGroup) {
      return;
    }

    // ✅ FIX 2026-03-06: Déterminer dynamiquement le nom du type de notice dans le FormGroup
    const firstKey = noticeTypeName?.toLowerCase() || this.detectNoticeTypeFromFormGroup(formGroup);


    // Configuration des chemins pour chaque type d'élément duplicable
    const pathConfig: { [key: string]: string[] } = {
      'RES': [firstKey, 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lotresult'],
      'ORG': [firstKey, 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'organizations', 'organization'],
      'TEN': [firstKey, 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'lottender'],
      'CON': [firstKey, 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'settledcontract'],
      'TPA': [firstKey, 'ublextensions', 'ublextension', '0', 'extensioncontent', 'eformsextension', 'noticeresult', 'tenderingparty']
    };

    const path = pathConfig[type];
    if (!path) {
      return;
    }

    // Naviguer jusqu'au parent du tableau et supprimer le tableau
    let current: any = formGroup;
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      if (current instanceof FormGroup && current.controls[key]) {
        current = current.controls[key];
      } else if (current instanceof FormArray && !isNaN(Number(key))) {
        current = current.at(Number(key));
      } else {
        // Le chemin n'existe pas encore, rien à supprimer
        return;
      }
    }

    // Supprimer le dernier élément du chemin (le FormArray)
    const lastKey = path[path.length - 1];
    if (current instanceof FormGroup && current.controls[lastKey]) {
      const controlToRemove = current.controls[lastKey];
      if (controlToRemove instanceof FormArray) {
        while (controlToRemove.length > 0) {
          controlToRemove.removeAt(0);
        }
      }
    }
  }

  /**
   * ✅ FIX 2026-03-06: Détecte dynamiquement le type de notice depuis le FormGroup.
   * Cherche parmi les noms connus de notices (contractnotice, contractawardnotice,
   * priorinformationnotice, businessregistrationinformationnotice) dans les contrôles du FormGroup.
   * Fallback vers 'contractawardnotice' si aucun n'est trouvé.
   */
  private detectNoticeTypeFromFormGroup(formGroup: FormGroup): string {
    const knownNoticeTypes = [
      'contractnotice',
      'contractawardnotice',
      'priorinformationnotice',
      'businessregistrationinformationnotice'
    ];

    // Chercher directement dans les contrôles du formGroup
    const controlKeys = Object.keys(formGroup.controls || {});
    for (const noticeType of knownNoticeTypes) {
      if (controlKeys.includes(noticeType)) {
        return noticeType;
      }
    }

    // Chercher dans le sous-groupe 'eforms' si présent
    const eformsGroup = formGroup.get('eforms') as FormGroup;
    if (eformsGroup) {
      const eformsKeys = Object.keys(eformsGroup.controls || {});
      for (const noticeType of knownNoticeTypes) {
        if (eformsKeys.includes(noticeType)) {
          return noticeType;
        }
      }
    }

    // Fallback
    return 'contractawardnotice';
  }
}
